// Pasta-water technique validator. Three rules every pasta recipe
// should honor:
//
//   1. SALT THE WATER. ≥1% salinity ("as salty as the sea"). Salt
//      added at the pot, not as a sprinkle on the finished plate.
//
//   2. RESERVE PASTA WATER. The starchy water emulsifies sauces.
//      Pulling a cup out before draining is the single biggest
//      thing separating restaurant-quality pasta from boiled-and-
//      sauced pasta.
//
//   3. DON'T RINSE. Rinsing strips the starch the sauce needs to
//      cling. (Exception: cold pasta salad — which is a separate
//      recipe category we don't validate here.)
//
// Sister to baking validator; same { ok, issues, warnings } shape;
// auto-fix repairs the boil step's instruction in place.

const PASTA_CANONICALS = new Set([
  "pasta", "spaghetti", "penne", "rigatoni", "fettuccine",
  "linguine", "tagliatelle", "pappardelle", "conchiglie", "farfalle",
  "fusilli", "rotini", "orecchiette", "bucatini", "lasagna_noodles",
  "macaroni", "elbow_pasta", "ditalini", "orzo", "gnocchi",
  "tortellini", "ravioli", "angel_hair", "capellini", "ziti",
  "cavatappi", "campanelle", "shells", "egg_noodles",
]);

const BOIL_VERB_RE = /\b(boil|cook)\b/i;
const SALT_CUE_RE = /\b(salt(?:ed)?|salty|saline|seasoned\s+water|kosher\s+salt|sea\s+salt)\b/i;
const RESERVE_CUE_RE = /\b(reserve|save|set\s+aside)\s+(?:a\s+)?(?:cup|ladle|½|1\/2|some|the)?\s*(?:of\s+)?(?:the\s+)?(?:pasta\s+)?water/i;

function stepText(s) {
  return s?.instruction || s?.body || s?.text || "";
}

function findPastaInRecipe(recipe) {
  const ings = recipe?.ingredients || [];
  for (const ing of ings) {
    const id = ing?.ingredientId;
    if (id && PASTA_CANONICALS.has(id)) return id;
  }
  return null;
}

function findBoilStep(recipe, pastaId) {
  const steps = recipe?.steps || [];
  const pastaWord = (pastaId || "pasta").replace(/_/g, " ");
  // Cook step is the one with both a boil verb AND a pasta mention
  // (or the canonical "pasta" word).
  for (let i = 0; i < steps.length; i += 1) {
    const txt = stepText(steps[i]).toLowerCase();
    if (!BOIL_VERB_RE.test(txt)) continue;
    if (txt.includes("pasta") || (pastaWord && txt.includes(pastaWord))) {
      return i;
    }
  }
  return -1;
}

// Cooked-in-liquid dishes — the noodles simmer IN the broth, are never
// drained, and there's no pan sauce. The reserve-water + salt-the-water
// rules assume boil → drain → sauce, so they're nonsense here (chicken
// noodle soup, ramen, minestrone, pho, one-pot pasta). Skip the whole
// check when detected.
const SOUP_RE = /\b(soups?|stews?|chowders?|broths?|brodo|minestrone|ramen|pho|laksa|congee|one[-\s]?pot)\b/i;
const BROTH_COOK_RE = /\b(broth|stock)\b/i;

// A sauce is the WHOLE reason to reserve starchy pasta water — it's the
// emulsifier. No sauce → nothing to save it for. Covers named sauces and
// emulsion dishes (cacio e pepe, carbonara) plus the "toss/coat the pasta"
// finish that implies one.
const SAUCE_RE = /\b(sauce|emulsif\w*|pesto|marinara|alfredo|carbonara|cacio\s*e\s*pepe|rag[uù]|bolognese|gravy|cream\s+sauce|pan\s+sauce|toss(?:ed|ing)?\s+(?:the\s+)?(?:pasta|noodles|spaghetti)|coat(?:s|ed|ing)?\s+(?:the\s+)?(?:pasta|noodles|spaghetti))\b/i;

function recipeMeta(recipe) {
  const tags = Array.isArray(recipe?.tags) ? recipe.tags.join(" ") : "";
  return `${recipe?.title || ""} ${recipe?.subtitle || ""} ${tags} ${recipe?.category || ""}`;
}
function allStepText(recipe) {
  return (recipe?.steps || []).map(stepText).join("  ");
}
// True when the pasta cooks in liquid and is never drained into a sauce.
function cooksInLiquid(recipe, boilIdx) {
  if (SOUP_RE.test(recipeMeta(recipe))) return true;
  const boilTxt = stepText((recipe?.steps || [])[boilIdx] || {});
  // "...add the noodles to the broth and simmer..." — cooked in liquid.
  if (BROTH_COOK_RE.test(boilTxt)) return true;
  return false;
}
function recipeHasSauce(recipe) {
  return SAUCE_RE.test(allStepText(recipe)) || SAUCE_RE.test(recipeMeta(recipe));
}

export function isPastaRecipe(recipe) {
  return findPastaInRecipe(recipe) != null;
}

export function validatePastaWater(recipe) {
  const pastaId = findPastaInRecipe(recipe);
  if (!pastaId) return { ok: true, issues: [], warnings: [] };
  const boilIdx = findBoilStep(recipe, pastaId);
  if (boilIdx === -1) return { ok: true, issues: [], warnings: [] };
  // Cooked-in-liquid (soup / ramen / one-pot): noodles simmer in the
  // broth — no draining, no sauce — so neither rule applies. Bail.
  if (cooksInLiquid(recipe, boilIdx)) return { ok: true, issues: [], warnings: [] };
  const step = recipe.steps[boilIdx];
  const txt = stepText(step);
  const warnings = [];
  if (!SALT_CUE_RE.test(txt)) {
    warnings.push({
      kind: "pasta_water_unsalted",
      severity: "warning",
      stepIndex: boilIdx,
      stepId: step.id,
      message: `Step "${step.title || step.id}" boils pasta but doesn't say to salt the water.`,
      suggestion: "Add \"salt heavily — the water should taste like the sea (~1 tbsp per quart)\" to the boil step.",
    });
  }
  // Reserve pasta water ONLY when there's a sauce to emulsify. No sauce
  // (plain boiled / buttered / dressed noodles) → nothing to save it for.
  if (recipeHasSauce(recipe) && !RESERVE_CUE_RE.test(txt)) {
    warnings.push({
      kind: "pasta_water_not_reserved",
      severity: "warning",
      stepIndex: boilIdx,
      stepId: step.id,
      message: `Step "${step.title || step.id}" doesn't tell the cook to reserve some pasta water for the sauce.`,
      suggestion: "Append \"reserve 1 cup of the starchy pasta water before draining\" so the sauce can emulsify.",
    });
  }
  return { ok: true, issues: [], warnings };
}

export function autoFixPastaWater(recipe, findings) {
  const warnings = Array.isArray(findings?.warnings) ? findings.warnings : [];
  if (warnings.length === 0) return { recipe, changes: [] };
  const steps = [...(recipe.steps || [])];
  const changes = [];
  const groupedByStep = new Map();
  for (const w of warnings) {
    if (w.stepIndex == null || w.stepIndex < 0) continue;
    const arr = groupedByStep.get(w.stepIndex) || [];
    arr.push(w);
    groupedByStep.set(w.stepIndex, arr);
  }
  for (const [idx, ws] of groupedByStep) {
    if (idx >= steps.length) continue;
    const step = { ...steps[idx] };
    const orig = stepText(step).trim();
    let appended = "";
    for (const w of ws) {
      if (w.kind === "pasta_water_unsalted") {
        appended += " Salt the water heavily — it should taste like the sea (~1 tbsp kosher salt per quart).";
      }
      if (w.kind === "pasta_water_not_reserved") {
        appended += " Before draining, reserve 1 cup of the starchy pasta water — the sauce needs it to emulsify.";
      }
    }
    if (appended) {
      const next = orig + appended;
      if ("instruction" in step) step.instruction = next;
      else if ("body" in step)   step.body        = next;
      else                       step.instruction = next;
      steps[idx] = step;
      if (ws.some((w) => w.kind === "pasta_water_unsalted")) {
        changes.push({ kind: "pasta_water_unsalted", summary: "Added salting cue (\"like the sea\") to the pasta-boil step." });
      }
      if (ws.some((w) => w.kind === "pasta_water_not_reserved")) {
        changes.push({ kind: "pasta_water_not_reserved", summary: "Added \"reserve 1 cup of pasta water before draining\" to the boil step." });
      }
    }
  }
  return { recipe: { ...recipe, steps }, changes };
}
